import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, CircularProgress
} from '@mui/material';
import { ChevronLeft, ChevronRight, Music, User as UserIcon } from 'lucide-react';
import moment from 'moment';
import { toastError, toastSuccess } from '../../utils';
import ActionButton from '../../components/ActionButton';
import EmptyState from '../../components/EmptyState';
import CustomShowModal from '../Common/customShowModal';
import * as actions from './actions';

export default function ContentTransfer(props) {
  const [releaseId, setReleaseId] = useState('');
  const [toUserId, setToUserId] = useState('');
  const [releaseInfo, setReleaseInfo] = useState(null);
  const [releaseLoading, setReleaseLoading] = useState(false);
  const [releaseError, setReleaseError] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState('');
  const [preview, setPreview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 30;

  const releaseTimer = useRef(null);
  const userTimer = useRef(null);

  useEffect(() => {
    document.title = 'FM | Content Transfer';
    fetchHistory(1);
    return () => { document.title = 'Fronic Media Admin'; };
  }, []);

  const fetchHistory = (p) => {
    actions.getContentTransferHistoryAdmin({ page: p, limit })
      .then((res) => {
        setHistory(res.body || []);
        setTotal(res.total || 0);
        setPage(p);
      })
      .catch(() => {});
  };

  const onReleaseIdChange = useCallback((val) => {
    setReleaseId(val);
    setReleaseInfo(null);
    setReleaseError('');
    if (releaseTimer.current) clearTimeout(releaseTimer.current);
    if (!val || !val.trim()) return;
    releaseTimer.current = setTimeout(() => {
      setReleaseLoading(true);
      actions.lookupReleaseForTransferAdmin(val)
        .then((res) => {
          setReleaseLoading(false);
          if (res.status === 200) {
            setReleaseInfo(res.body);
          } else {
            setReleaseError(res.message || 'Release not found.');
          }
        })
        .catch((err) => {
          setReleaseLoading(false);
          setReleaseError(err?.message || 'Lookup failed.');
        });
    }, 600);
  }, []);

  const onUserIdChange = useCallback((val) => {
    setToUserId(val);
    setUserInfo(null);
    setUserError('');
    if (userTimer.current) clearTimeout(userTimer.current);
    if (!val || !val.trim()) return;
    userTimer.current = setTimeout(() => {
      setUserLoading(true);
      actions.lookupUserForTransferAdmin(val)
        .then((res) => {
          setUserLoading(false);
          if (res.status === 200) {
            setUserInfo(res.body);
          } else {
            setUserError(res.message || 'User not found.');
          }
        })
        .catch((err) => {
          setUserLoading(false);
          setUserError(err?.message || 'Lookup failed.');
        });
    }, 600);
  }, []);

  const onPreview = () => {
    if (!releaseId || !toUserId) {
      toastError('Please enter both Release ID and Target User ID.');
      return;
    }
    if (releaseError || userError) {
      toastError('Please fix the errors before proceeding.');
      return;
    }
    props.setLoader(true);
    actions.previewContentTransferAdmin({ release_id: releaseId, to_user_id: toUserId })
      .then((res) => {
        props.setLoader(false);
        if (res.status === 200) {
          setPreview(res.body);
          setShowModal(true);
        } else {
          toastError(res.message || 'Failed to fetch preview.');
        }
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to fetch preview.');
      });
  };

  const onTransfer = () => {
    props.setLoader(true);
    setShowModal(false);
    actions.contentTransferAdmin({ release_id: releaseId, to_user_id: toUserId })
      .then((res) => {
        props.setLoader(false);
        if (res.status === 200) {
          toastSuccess(res.message || 'Transfer successful.');
          setReleaseId('');
          setToUserId('');
          setReleaseInfo(null);
          setUserInfo(null);
          setPreview(null);
          fetchHistory(page);
        } else {
          toastError(res.message || 'Transfer failed.');
        }
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Transfer failed.');
      });
  };

  const totalPages = Math.ceil(total / limit) || 1;

  const cellSx = { color: '#CBD5E1', fontSize: '0.82rem', borderBottom: '1px solid #1E2130', py: 1.5 };
  const headCellSx = { color: '#64748B', fontSize: '0.75rem', fontWeight: 600, borderBottom: '1px solid #2E3247', textTransform: 'uppercase', letterSpacing: '0.04em' };

  return (
    <Box>
      {/* Transfer Form */}
      <Box sx={{
        backgroundColor: '#1A1D27', borderRadius: '12px', border: '1px solid #2E3247',
        p: 3, mb: 3,
      }}>
        <Typography sx={{ color: '#F1F5F9', fontSize: '1rem', fontWeight: 600, mb: 2.5 }}>
          Transfer Content Ownership
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {/* Release ID input + info card */}
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <TextField
              label="Release ID"
              type="number"
              size="small"
              fullWidth
              value={releaseId}
              onChange={(e) => onReleaseIdChange(e.target.value)}
              InputProps={{
                endAdornment: releaseLoading ? <CircularProgress size={16} sx={{ color: '#6C63FF' }} /> : null,
              }}
            />
            {releaseInfo && (
              <Box sx={{
                mt: 1.5, p: 1.5, borderRadius: '8px',
                backgroundColor: 'rgba(108, 99, 255, 0.06)', border: '1px solid rgba(108, 99, 255, 0.15)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
                  <Music size={14} color="#6C63FF" />
                  <Typography sx={{ color: '#F1F5F9', fontSize: '0.85rem', fontWeight: 600 }}>
                    {releaseInfo.release_title || 'Untitled'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pl: 0.2 }}>
                  <MiniInfo label="Status" value={releaseInfo.status} />
                  <MiniInfo label="Tracks" value={releaseInfo.number_of_tracks} />
                  <MiniInfo label="Owner" value={releaseInfo.owner_name || '-'} />
                  <MiniInfo label="Owner Email" value={releaseInfo.owner_email || '-'} />
                </Box>
              </Box>
            )}
            {releaseError && (
              <Typography sx={{ color: '#EF4444', fontSize: '0.78rem', mt: 0.8, pl: 0.5 }}>
                {releaseError}
              </Typography>
            )}
          </Box>

          {/* Target User ID input + info card */}
          <Box sx={{ flex: 1, minWidth: 260 }}>
            <TextField
              label="Target User ID"
              type="number"
              size="small"
              fullWidth
              value={toUserId}
              onChange={(e) => onUserIdChange(e.target.value)}
              InputProps={{
                endAdornment: userLoading ? <CircularProgress size={16} sx={{ color: '#6C63FF' }} /> : null,
              }}
            />
            {userInfo && (
              <Box sx={{
                mt: 1.5, p: 1.5, borderRadius: '8px',
                backgroundColor: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
                  <UserIcon size={14} color="#10B981" />
                  <Typography sx={{ color: '#F1F5F9', fontSize: '0.85rem', fontWeight: 600 }}>
                    {userInfo.name || 'Unknown'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, pl: 0.2 }}>
                  <MiniInfo label="Email" value={userInfo.email || '-'} />
                  <MiniInfo label="Joined" value={userInfo.created_at ? moment(userInfo.created_at).format('DD MMM YYYY') : '-'} />
                </Box>
              </Box>
            )}
            {userError && (
              <Typography sx={{ color: '#EF4444', fontSize: '0.78rem', mt: 0.8, pl: 0.5 }}>
                {userError}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Button centered below */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
          <ActionButton
            variant="success"
            onClick={onPreview}
            disabled={!releaseId || !toUserId || releaseLoading || userLoading || !!releaseError || !!userError}
          >
            Confirm Transfer
          </ActionButton>
        </Box>
      </Box>

      {/* Confirmation Modal */}
      <CustomShowModal
        open={showModal}
        handleClose={() => setShowModal(false)}
        title="Confirm Content Transfer"
        maxWidth="sm"
      >
        {preview && (
          <Box sx={{ p: 1 }}>
            <InfoSection title="Release">
              <InfoRow label="ID" value={preview.release.release_id} />
              <InfoRow label="Title" value={preview.release.release_title} />
              <InfoRow label="Status" value={preview.release.status} />
            </InfoSection>
            <InfoSection title="From Account (Current Owner)">
              <InfoRow label="Name" value={preview.fromUser.name} />
              <InfoRow label="Email" value={preview.fromUser.email} />
              <InfoRow label="Account Created" value={preview.fromUser.created_at ? moment(preview.fromUser.created_at).format('DD MMM YYYY') : '-'} />
            </InfoSection>
            <InfoSection title="To Account (New Owner)">
              <InfoRow label="Name" value={preview.toUser.name} />
              <InfoRow label="Email" value={preview.toUser.email} />
              <InfoRow label="Account Created" value={preview.toUser.created_at ? moment(preview.toUser.created_at).format('DD MMM YYYY') : '-'} />
            </InfoSection>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2.5 }}>
              <ActionButton variant="danger" onClick={() => setShowModal(false)}>Cancel</ActionButton>
              <ActionButton variant="success" onClick={onTransfer}>Transfer</ActionButton>
            </Box>
          </Box>
        )}
      </CustomShowModal>

      {/* Transfer History */}
      <Box sx={{
        backgroundColor: '#1A1D27', borderRadius: '12px', border: '1px solid #2E3247',
        overflow: 'hidden',
      }}>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #2E3247' }}>
          <Typography sx={{ color: '#F1F5F9', fontSize: '1rem', fontWeight: 600 }}>
            Transfer History
          </Typography>
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Release ID</TableCell>
              <TableCell sx={headCellSx}>Release Name</TableCell>
              <TableCell sx={headCellSx}>From Account</TableCell>
              <TableCell sx={headCellSx}>To Account</TableCell>
              <TableCell sx={headCellSx}>Transferred By</TableCell>
              <TableCell sx={headCellSx}>Transfer Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 ? (
              <EmptyState message="No transfer history" colSpan={6} />
            ) : (
              history.map((row) => (
                <TableRow key={row.transfer_id} sx={{ '&:hover': { backgroundColor: 'rgba(108,99,255,0.04)' } }}>
                  <TableCell sx={cellSx}>{row.release_id}</TableCell>
                  <TableCell sx={cellSx}>{row.release_title || '-'}</TableCell>
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: '0.82rem', color: '#F1F5F9' }}>{row.from_user_name}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>{row.from_user_email}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: '0.82rem', color: '#F1F5F9' }}>{row.to_user_name}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>{row.to_user_email}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx}>{row.transferred_by}</TableCell>
                  <TableCell sx={cellSx}>{row.transferred_at ? moment(row.transferred_at).format('DD MMM YYYY, hh:mm A') : '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {total > limit && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: 3, py: 1.5, borderTop: '1px solid #2E3247', gap: 1 }}>
            <IconButton
              disabled={page <= 1}
              onClick={() => fetchHistory(page - 1)}
              sx={{ color: '#94A3B8', '&.Mui-disabled': { color: '#2E3247' } }}
              size="small"
            >
              <ChevronLeft size={18} />
            </IconButton>
            <Typography sx={{ color: '#94A3B8', fontSize: '0.8rem' }}>
              Page {page} of {totalPages}
            </Typography>
            <IconButton
              disabled={page >= totalPages}
              onClick={() => fetchHistory(page + 1)}
              sx={{ color: '#94A3B8', '&.Mui-disabled': { color: '#2E3247' } }}
              size="small"
            >
              <ChevronRight size={18} />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function MiniInfo({ label, value }) {
  return (
    <Box>
      <Typography sx={{ color: '#64748B', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
        {label}
      </Typography>
      <Typography sx={{ color: '#CBD5E1', fontSize: '0.8rem' }}>
        {value ?? '-'}
      </Typography>
    </Box>
  );
}

function InfoSection({ title, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ color: '#6C63FF', fontSize: '0.8rem', fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {title}
      </Typography>
      <Box sx={{ pl: 1 }}>{children}</Box>
    </Box>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
      <Typography sx={{ color: '#64748B', fontSize: '0.82rem', minWidth: 120 }}>{label}:</Typography>
      <Typography sx={{ color: '#F1F5F9', fontSize: '0.82rem' }}>{value || '-'}</Typography>
    </Box>
  );
}
